import React from "react";
import { IAttachment, UserTextChatItem } from "@fidget/common/models/chat/chatItems";
import { FilePreviews } from "@fidget/common/components/files/filePreviews";
import { BackgroundType, ChatItemBorder } from "@fidget/common/components/chat/chatItemBorder";
import styles from "./chatMessage.css";
import { PickerWidgetMessages } from "@fidget/common/models/pickerWidgetMessages";
import { observer } from "mobx-react";
import { useForeContext } from "../../models";
import { MessageWidgets } from "./messageWidgets";
import { WidgetFormSubmissionWithWidget } from "@closerplatform/spinner-openapi";
import { userActions } from "../../actions/user";
import {
  isHTMLAnchorElement,
  linkOnClick,
  ParsedMessage
} from "@fidget/common/components/mstchat/utils";
import { RichMessage } from "./richMessages/RichMessageTypes";
import { extractRichMessageFromContext } from "./richMessages/utils";
import { staticConfig } from "@fidget/common/config/static-config";
import { findLast, takeRight } from "lodash";
import { ChatItemGroup } from "@fidget/common/models/chat/chatItemGroup";
import { RichMessageView } from "./richMessages/richMessage/RichMessageView";

const showdown = require("showdown");
const converter = new showdown.Converter({
  simplifiedAutoLink: true,
  simpleLineBreaks: true,
  tables: true
});

interface Props {
  messageId: string;
  text?: string;
  attachments?: Array<IAttachment>;
  context?: any;
  fromAgent?: boolean;
  fromBot?: boolean;
  alignRight: boolean;
  withCallButton?: boolean;
  scrollToBottom?: () => void;
  withCobrowsingDialog?: boolean;
  submission?: WidgetFormSubmissionWithWidget;
  offsetScroll?: (value: number) => void;
  isMobile?: boolean;
}

export const ChatMessage = observer(
  ({
    messageId,
    text,
    attachments,
    fromAgent,
    fromBot,
    alignRight,
    withCallButton,
    scrollToBottom,
    withCobrowsingDialog,
    submission,
    context: messageContext,
    isMobile
  }: Props) => {
    const findWidgetMessages = (message?: string, widgetMessages?: PickerWidgetMessages) => {
      if (widgetMessages !== undefined && message !== undefined) {
        for (let q in widgetMessages) {
          if (message.includes(q)) {
            return q;
          }
        }
      }
      return undefined;
    };

    const context = useForeContext();

    const shouldDisableTextButtons = (): boolean => {
      const history = context.state.room.chat.history;

      const lastGroupChatItem = findLast(
        takeRight(history, staticConfig.widgetPageSize),
        (chatItem) => ChatItemGroup.isChatItemGroup(chatItem)
      );

      if (!lastGroupChatItem) {
        return false;
      }

      if (!ChatItemGroup.isChatItemGroup(lastGroupChatItem)) {
        return false;
      }

      const lastTextChatItem = findLast(lastGroupChatItem.items, (chatItem) =>
        UserTextChatItem.isUserTextChatItem(chatItem)
      );

      if (!lastTextChatItem || !UserTextChatItem.isUserTextChatItem(lastTextChatItem)) {
        return false;
      }

      if (lastTextChatItem.id === messageId) {
        return false;
      }

      return true;
    };

    const { pickerWidgetMessages } = context!.state!;
    const question = findWidgetMessages(text, pickerWidgetMessages);
    const messagesWithCallbacksExists =
      pickerWidgetMessages && question && pickerWidgetMessages[question]!.length > 0;

    const widgetsAvailable = messagesWithCallbacksExists || withCallButton || withCobrowsingDialog;

    const onFileDownload = (fileUrl: string, fileId?: string, fileName?: string) => {
      userActions.sendFileDownloaded(context, fileUrl, fileId, fileName);
    };

    const renderAttachments = () =>
      attachments && (
        <div className={styles.attachments}>
          <FilePreviews
            attachments={attachments}
            alignRight={alignRight}
            widgetStyle
            onFileDownload={onFileDownload}
          />
        </div>
      );

    const renderText = () => {
      const onLinkClicked = (link: string) => {
        userActions.sendLinkClicked(context, link);
      };

      const richText = prepareRichText({ text, fromBot, fromAgent });
      const backgroundType = getBorderBackgroundType(alignRight);

      const richMessage: RichMessage | undefined = extractRichMessageFromContext(messageContext);

      return (
        <>
          {richText && (
            <ChatItemBorder widgetStyle backgroundType={backgroundType}>
              <div className={styles.text}>
                <div
                  className={styles["message-body"]}
                  onClick={(e) => {
                    if (isHTMLAnchorElement(e.target)) {
                      onLinkClicked(e.target.href);
                    }
                  }}
                >
                  {richText}
                </div>
              </div>
              {widgetsAvailable && (
                <MessageWidgets
                  messageId={messageId}
                  text={text}
                  alignRight={alignRight}
                  pickerWidgetMessages={pickerWidgetMessages}
                  question={question}
                  submission={submission}
                  withCobrowsingDialog={withCobrowsingDialog}
                  withCallButton={withCallButton}
                  scrollToBottom={scrollToBottom}
                />
              )}
            </ChatItemBorder>
          )}
          {!!submission && (
            <ChatItemBorder widgetStyle backgroundType={backgroundType}>
              <MessageWidgets
                messageId={messageId}
                alignRight={alignRight}
                submission={submission}
                scrollToBottom={scrollToBottom}
              />
            </ChatItemBorder>
          )}
          {richMessage && (
            <>
              {richMessage.richCards &&
              richMessage.richCards.length === 1 &&
              !richMessage.buttons &&
              richMessage.richCards[0]?.actions &&
              !richMessage.richCards[0].media &&
              (!richMessage.richCards[0].descriptionPairs ||
                richMessage.richCards[0].descriptionPairs.length === 0) ? (
                <RichMessageView
                  richCards={[]}
                  buttons={richMessage.richCards[0].actions}
                  disableTextButtons={shouldDisableTextButtons()}
                />
              ) : (
                <RichMessageView
                  richCards={richMessage.richCards ? richMessage.richCards : []}
                  buttons={richMessage.buttons ? richMessage.buttons : []}
                  disableTextButtons={shouldDisableTextButtons()}
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        </>
      );
    };

    const prepareRichText = (args: { text?: string; fromBot?: boolean; fromAgent?: boolean }) => {
      if (args.text) {
        if (args.fromBot) {
          return (
            <div
              onClick={linkOnClick}
              dangerouslySetInnerHTML={{ __html: converter.makeHtml(args.text) }}
            />
          );
        }
        if (args.fromAgent) {
          return <ParsedMessage text={args.text} markdown={true} />;
        }
        return (
          <>
            {args.text.split("\n").map((part, idx) => (
              <p key={idx}>{part}</p>
            ))}
          </>
        );
      }
      return args.text;
    };

    const getBorderBackgroundType = (isUserMessage: boolean): BackgroundType => {
      if (isUserMessage) {
        return BackgroundType.CURRENT_USER_OR_ORG;
      }
      return BackgroundType.AGENT;
    };

    return (
      <>
        {renderAttachments()}
        {renderText()}
      </>
    );
  }
);
